import { createImmerStore, freeze } from "@expert/state-utils";

import type { Customer, DeviceReplacement } from "../types";

import { getIsProd } from "../config";

export interface CustomerState {
    customer?: Customer;
    deviceReplacement?: DeviceReplacement;
    hasPrimaryAccountHolder: boolean;
    isEditingVerificationInput: boolean;
    isCustomerVerificationComplete: boolean;
    asurionAgreements: string[];
    partnerAgreements: string[];
}

interface VerifiedCustomerStore {
    verifiedCustomers: Record<string, CustomerState>;
    unverifiedCustomerData: Record<string, Partial<Customer>>;
    getVerifiedCustomer: (sessionId: string) => CustomerState;
    getUnverifiedCustomerData: (sessionId: string) => Partial<Customer>;
    setCustomer: (
        sessionId: string,
        customer?: Partial<Customer & { mdn: string }>,
        deviceReplacement?: DeviceReplacement,
    ) => void;
    setUnverifiedCustomerData: (sessionId: string, customerData: Partial<Customer>) => void;
    setIsEditingVerificationInput: (sessionId: string, isEditing: boolean) => void;
    setIsCustomerVerificationComplete: (sessionId: string, isComplete: boolean) => void;
    setHasPrimaryAccountHolder: (sessionId: string, value: boolean) => void;
    setDeviceReplacement: (sessionId: string, deviceReplacement?: DeviceReplacement) => void;
    setAsurionAgreements: (sessionId: string, products?: string[]) => void;
    setPartnerAgreements: (sessionId: string, products?: string[]) => void;
    reset: (sessionId: string) => void;
}

const EMPTY_AGREEMENTS: string[] = freeze([]);

const defaultCustomerState: CustomerState = freeze({
    customer: undefined,
    deviceReplacement: undefined,
    hasPrimaryAccountHolder: false,
    isEditingVerificationInput: true,
    isCustomerVerificationComplete: false,
    asurionAgreements: EMPTY_AGREEMENTS,
    partnerAgreements: EMPTY_AGREEMENTS,
});

const EMPTY_UNVERIFIED_DATA: Partial<Customer> = freeze({});

/**
 * Per-session verified-customer store: verified `Customer` data plus the
 * in-progress `unverifiedCustomerData` collected during verification.
 *
 * Prefer the exported selector hooks: they narrow the subscription to one field.
 * Missing-session reads resolve to frozen module-level sentinels, so referential
 * equality is stable.
 */
export const useVerifiedCustomerStore = createImmerStore<VerifiedCustomerStore>({
    devtools: { store: "verified-customer-store", name: "workspace/sales-tools", isProd: getIsProd() },
    persist: {
        name: "verified-customer-store",
        skipHydration: true,
        partialize: (state) => ({
            verifiedCustomers: state.verifiedCustomers,
            unverifiedCustomerData: state.unverifiedCustomerData,
        }),
    },
})((set, get) => ({
    verifiedCustomers: {},
    unverifiedCustomerData: {},
    getVerifiedCustomer: (sessionId): CustomerState => {
        return get().verifiedCustomers[sessionId] ?? defaultCustomerState;
    },
    getUnverifiedCustomerData: (sessionId): Partial<Customer> => {
        return get().unverifiedCustomerData[sessionId] ?? EMPTY_UNVERIFIED_DATA;
    },
    setCustomer: (sessionId, customer?, deviceReplacement?) =>
        set((state) => {
            const currentVerifiedCustomer = state.getVerifiedCustomer(sessionId);
            state.verifiedCustomers[sessionId] = {
                ...currentVerifiedCustomer,
                customer: {
                    ...currentVerifiedCustomer.customer,
                    ...customer,
                },
                deviceReplacement: deviceReplacement ?? currentVerifiedCustomer.deviceReplacement,
            };
            delete state.unverifiedCustomerData[sessionId];
        }),
    setUnverifiedCustomerData: (sessionId, customerData) => {
        set((state) => {
            state.unverifiedCustomerData[sessionId] = {
                ...state.unverifiedCustomerData[sessionId],
                ...customerData,
            };
        });
    },
    setDeviceReplacement: (sessionId, deviceReplacement) =>
        set((state) => {
            const currentVerifiedCustomer = state.getVerifiedCustomer(sessionId);
            state.verifiedCustomers[sessionId] = {
                ...currentVerifiedCustomer,
                deviceReplacement,
            };
        }),
    setIsEditingVerificationInput: (sessionId, isEditing) =>
        set((state) => {
            const currentVerifiedCustomer = state.getVerifiedCustomer(sessionId);
            state.verifiedCustomers[sessionId] = {
                ...currentVerifiedCustomer,
                isEditingVerificationInput: isEditing,
            };
        }),
    setIsCustomerVerificationComplete: (sessionId, isComplete) =>
        set((state) => {
            const currentVerifiedCustomer = state.getVerifiedCustomer(sessionId);
            state.verifiedCustomers[sessionId] = {
                ...currentVerifiedCustomer,
                isCustomerVerificationComplete: isComplete,
            };
            if (isComplete) {
                delete state.unverifiedCustomerData[sessionId];
            }
        }),
    setHasPrimaryAccountHolder: (sessionId, value) =>
        set((state) => {
            const currentVerifiedCustomer = state.getVerifiedCustomer(sessionId);
            state.verifiedCustomers[sessionId] = {
                ...currentVerifiedCustomer,
                hasPrimaryAccountHolder: value,
            };
        }),
    setAsurionAgreements: (sessionId, products?) => {
        set((state) => {
            const currentVerifiedCustomer = state.getVerifiedCustomer(sessionId);
            state.verifiedCustomers[sessionId] = {
                ...currentVerifiedCustomer,
                asurionAgreements: products ?? EMPTY_AGREEMENTS,
            };
        });
    },
    setPartnerAgreements: (sessionId, products?) => {
        set((state) => {
            const currentVerifiedCustomer = state.getVerifiedCustomer(sessionId);
            state.verifiedCustomers[sessionId] = {
                ...currentVerifiedCustomer,
                partnerAgreements: products ?? EMPTY_AGREEMENTS,
            };
        });
    },
    reset: (sessionId) =>
        set((state) => {
            delete state.verifiedCustomers[sessionId];
            delete state.unverifiedCustomerData[sessionId];
        }),
}));

/** Verified `Customer` for a session, or `undefined` if not yet verified. */
export const useVerifiedCustomer = (sessionId: string) =>
    useVerifiedCustomerStore((state) => state.getVerifiedCustomer(sessionId).customer);

/** Device replacement details for a session, or `undefined` if none has been set. */
export const useDeviceReplacement = (sessionId: string) =>
    useVerifiedCustomerStore((state) => state.getVerifiedCustomer(sessionId).deviceReplacement);

/** Whether customer verification has completed for a session. */
export const useIsVerificationComplete = (sessionId: string) =>
    useVerifiedCustomerStore((state) => state.getVerifiedCustomer(sessionId).isCustomerVerificationComplete);
